import { useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { AI_OBJECTIVE_LABELS, ALL_AI_OPTIMIZATION_OBJECTIVES } from '../aiConfigure'
import type { AiObjectives, AiOptimizationObjective } from '../aiConfigure'

interface ObjectivesCardProps {
  objectives: AiObjectives
  onChange: (next: AiObjectives) => void
  onGenerate: () => void
  generating: boolean
}

// Presentational — AccountSetupStage owns the actual inferObjectives /
// configureFromScope calls, matching the same split used for
// AiConfigureChatPanel and AiSuggestionBanner. Editing here never calls the
// AI again by itself; the rep reviews/adjusts, then explicitly generates.
export function ObjectivesCard({ objectives, onChange, onGenerate, generating }: ObjectivesCardProps) {
  const [jurisdictionInput, setJurisdictionInput] = useState('')

  function addJurisdiction() {
    const v = jurisdictionInput.trim()
    if (!v || objectives.jurisdictions.includes(v)) { setJurisdictionInput(''); return }
    onChange({ ...objectives, jurisdictions: [...objectives.jurisdictions, v] })
    setJurisdictionInput('')
  }
  function removeJurisdiction(j: string) {
    onChange({ ...objectives, jurisdictions: objectives.jurisdictions.filter((x) => x !== j) })
  }

  return (
    <div className="rounded-[10px] p-3 space-y-2.5" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <div>
        <div className="flex items-center gap-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">What is this customer optimising for?</p>
          <span
            className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={objectives.stated
              ? { background: 'rgba(52,211,153,.15)', color: '#15803d' }
              : { background: 'rgba(99,102,241,.12)', color: '#4f46e5' }
            }
          >
            {objectives.stated ? 'Confirmed' : 'Recommended'}
          </span>
        </div>
        <p className="text-[10px] text-[#475569] leading-snug mt-1">
          {objectives.rationale} <span className="uppercase font-semibold text-[#334155]">({objectives.confidence} confidence)</span> — review and adjust before generating suggestions.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block space-y-1">
          <span className="block text-[9px] font-medium text-[#64748b] uppercase tracking-wide">Primary objective</span>
          <select
            value={objectives.primary}
            // A manual pick is now a confirmed choice, not merely the AI's
            // recommendation — reuse the same "settled" badge treatment as
            // an explicitly Scope-stated priority.
            onChange={(e) => onChange({ ...objectives, primary: e.target.value as AiOptimizationObjective, stated: true })}
            className="w-full text-[11px] px-2 py-1.5 rounded-[6px]"
            style={{ border: '1px solid #e5e7eb', background: '#ffffff', color: '#1e293b' }}
          >
            {ALL_AI_OPTIMIZATION_OBJECTIVES.map((o) => (
              <option key={o} value={o}>{AI_OBJECTIVE_LABELS[o]}</option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="block text-[9px] font-medium text-[#64748b] uppercase tracking-wide">Secondary objective</span>
          <select
            value={objectives.secondary ?? ''}
            onChange={(e) => onChange({ ...objectives, secondary: e.target.value ? (e.target.value as AiOptimizationObjective) : null, stated: true })}
            className="w-full text-[11px] px-2 py-1.5 rounded-[6px]"
            style={{ border: '1px solid #e5e7eb', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="">None</option>
            {ALL_AI_OPTIMIZATION_OBJECTIVES.filter((o) => o !== objectives.primary).map((o) => (
              <option key={o} value={o}>{AI_OBJECTIVE_LABELS[o]}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-1.5">
        <span className="block text-[9px] font-medium text-[#64748b] uppercase tracking-wide">Jurisdictions</span>
        <div className="flex flex-wrap gap-1.5">
          {objectives.jurisdictions.map((j) => (
            <span
              key={j}
              className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}
            >
              {j}
              <button type="button" onClick={() => removeJurisdiction(j)} aria-label={`Remove ${j}`} className="text-[#94a3b8] hover:text-[#475569]">
                <X size={9} />
              </button>
            </span>
          ))}
          {objectives.jurisdictions.length === 0 && (
            <span className="text-[10px] text-[#94a3b8] italic">None identified — add one below</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <input
            value={jurisdictionInput}
            onChange={(e) => setJurisdictionInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addJurisdiction() } }}
            placeholder="Add a market/jurisdiction"
            className="flex-1 text-[10px] px-2 py-1 rounded-[6px] outline-none"
            style={{ border: '1px solid #e5e7eb', background: '#ffffff', color: '#1e293b' }}
          />
          <button
            type="button"
            onClick={addJurisdiction}
            className="w-6 h-6 rounded-[6px] flex items-center justify-center shrink-0"
            style={{ background: '#f1f5f9', border: '1px solid #e5e7eb' }}
            aria-label="Add jurisdiction"
          >
            <Plus size={11} />
          </button>
        </div>
      </div>

      {objectives.regulatoryNotes && (
        <p className="text-[10px] text-[#475569] leading-snug italic">{objectives.regulatoryNotes}</p>
      )}

      <button
        type="button"
        onClick={onGenerate}
        disabled={generating}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-semibold text-white transition-all disabled:opacity-60"
        style={{ background: '#4f46e5', boxShadow: '0 3px 12px rgba(79,70,229,.35)' }}
      >
        {generating
          ? <><Loader2 size={12} className="animate-spin" /> Generating suggestions…</>
          : 'Generate suggestions with these objectives'}
      </button>
    </div>
  )
}
